/**
 * The performance domain's client-side vocabulary — the label/badge maps for a review
 * cycle's, a goal's and a review's enums.
 *
 * Nothing here decides anything about a request. The labels turn the contract's enums
 * into `MessageKey`s so the screen never hard-codes a status word, the same discipline
 * `hr/leave.ts` and `hr/onboarding.ts` keep.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { GoalStatus, ReviewCycleStatus, ReviewKind } from '../api/hr';

/** The name of a cycle's `status`, for the cycles list. */
export const REVIEW_CYCLE_STATUS_LABEL: Readonly<Record<ReviewCycleStatus, MessageKey>> = {
  draft: 'hr.performance.cycleStatus.draft',
  open: 'hr.performance.cycleStatus.open',
  closed: 'hr.performance.cycleStatus.closed',
};

/** The badge severity a cycle status reads as. `open` is the live, actionable one. */
export const REVIEW_CYCLE_STATUS_BADGE: Readonly<Record<ReviewCycleStatus, Severity>> = {
  draft: 'info',
  open: 'low',
  closed: 'medium',
};

/** The name of a goal's `status`, for the goals lists. */
export const GOAL_STATUS_LABEL: Readonly<Record<GoalStatus, MessageKey>> = {
  active: 'hr.performance.goalStatus.active',
  achieved: 'hr.performance.goalStatus.achieved',
  missed: 'hr.performance.goalStatus.missed',
  cancelled: 'hr.performance.goalStatus.cancelled',
};

/** The badge severity a goal status reads as — colour third, never alone (DESIGN.md §4). */
export const GOAL_STATUS_BADGE: Readonly<Record<GoalStatus, Severity>> = {
  active: 'info',
  achieved: 'low',
  missed: 'medium',
  cancelled: 'medium',
};

/** The name of a review's `kind` — who wrote it, relative to its subject. */
export const REVIEW_KIND_LABEL: Readonly<Record<ReviewKind, MessageKey>> = {
  self: 'hr.performance.reviewKind.self',
  manager: 'hr.performance.reviewKind.manager',
  peer: 'hr.performance.reviewKind.peer',
};
